import { customAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";
import { normalizeDomain } from "./normalizeDomain";

export const okta = defineConnector({
  identifier: "okta",
  name: "Okta",
  tagLine: "Identity and access management",
  keywords:
    "iam sso saml oidc scim identity access management users groups directory mfa",
  logo,
  protocol: "http",
  tags: ["featured", "it", "security", "identity"],
  defaultUrlPattern: ({ domain }) => {
    const normalized = normalizeDomain(domain);
    return normalized
      ? `https://${normalized.replace(/\./g, "\\.")}/.*`
      : "https://[^/]*\\.okta\\.com/.*";
  },
  auth: customAuth("okta"),
  steps: [
    {
      title: "Provide your Okta domain",
      items: [
        {
          kind: "markdown",
          content:
            "Enter your Okta organization’s URL. This should be the domain where you manage Okta applications.",
        },
        {
          kind: "hostname",
          label: "Okta domain",
          placeholder: "integrator-123456.okta.com",
          required: true,
          helperText: "Your Okta domain (e.g., example.okta.com)",
          target: { kind: "configuration", field: "domain" },
        },
      ],
    },
    {
      title: "Create the Okta application",
      items: [
        {
          kind: "markdown",
          content: ({ domain }: Record<string, string>) => {
            const applicationsLink = domain
              ? `[**Applications** in your Okta console](https://${domain}/admin/apps/active)`
              : "**Applications** at `https://<your-okta-domain>/admin/apps/active`";

            return `Visit ${applicationsLink}, select **Create App Integration**, choose **API services**, enter an app integration name, and save it.`;
          },
        },
      ],
    },
    {
      title: "Provide your client ID",
      items: [
        {
          kind: "markdown",
          content: "Copy the pre-generated client ID and paste it here.",
        },
        {
          kind: "input",
          label: "Client ID",
          placeholder: "0oa…",
          required: true,
          helperText: "Paste the client ID from the Okta app.",
          target: { kind: "configuration", field: "clientId" },
        },
      ],
    },
    {
      title: "Obtain the private key",
      items: [
        {
          kind: "markdown",
          content:
            "In **Client Credentials**, click **Edit** and select **Public key / Private key** for client authentication, then click **Save**.\n\nThen, in **Public keys**, click **Edit** and **Add key**, then **Generate new key**. Copy the JSON private key that is produced, then click **Done** and **Save**.",
        },
        {
          kind: "input",
          label: "Private key",
          placeholder: "Paste the RSA private JWK JSON object",
          required: true,
          helperText: "Paste the JSON private key that Okta generates.",
          target: { kind: "secrets", field: "privateKey" },
        },
      ],
    },
    {
      title: "Proof of possession",
      items: [
        {
          kind: "markdown",
          content:
            'Scroll down to **General Settings**. Click **Edit**, uncheck "Require Demonstrating Proof of Possession (DPoP) header in token requests", and click **Save**.',
        },
      ],
    },
    {
      title: "Scopes",
      items: [
        {
          kind: "markdown",
          content:
            "In the **Okta API Scopes** tab, select the scopes you want your application to have, then enter them here.",
        },
        {
          kind: "input",
          label: "Scopes",
          placeholder: "okta.users.read",
          required: true,
          helperText:
            "Enter space-separated Okta API scopes. `okta.users.read` is a good starting point for testing.",
          defaultValue: "okta.users.read",
          target: { kind: "configuration", field: "scopes" },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ domain }) => ({
      url: `https://${normalizeDomain(domain)}/.well-known/3b-okta-connection-test`,
      expectedStatus: [200, 400],
      expectedBodyContains: ["access_token", "consent_required"],
    }),
  },
  publicEnvVars: ({ domain }): Record<string, string> => {
    const normalized = normalizeDomain(domain);
    return normalized
      ? { OKTA_DOMAIN: normalized, OKTA_ORG_URL: `https://${normalized}` }
      : {};
  },
});
